/**
 * The W2 / W3 push payloads, and the two fallback renderings of them.
 *
 * Authority: `09-tms-write-paths.md` §1 (the three paths), §3 (the exact flow), §4 (the
 * per-path fallbacks) and §5.1 (the confirmation-and-receipt component);
 * `05-reporting-marking.md` §B4 (the marks push) and §B6 (the report push).
 *
 * **Why the payload lives in `packages/shared` and not in the API.** Three things must be
 * the same text, or the system tells a lie somewhere:
 *
 *  1. what the confirmation dialogue says will be pushed,
 *  2. what the gateway is handed and the receipt records,
 *  3. what `Copy For TMS` puts on the clipboard and what the Word download contains when
 *     the transport is unavailable and the TD does it by hand (§4).
 *
 * One builder and one formatter, used by the API and by the browser, is the only way those
 * three cannot drift. Nothing here talks to TMS, opens a socket or knows a URL — this module
 * is pure data. **The transport is a seam and stays one until spike week (17–21 Aug).**
 *
 * Ruling R1′: a payload names the official and the tournament, never a competition. Nothing
 * here aggregates anything from a match or a competition, because no such value exists.
 */
import { marksTotalsRow } from "./marks-totals-row.js";
import { CRITERIA_WEIGHT_TOTAL, computePerformanceOutcome } from "./marking-criteria.js";
/**
 * The marks payload, built from the official's own fixed set.
 *
 * The set drives the loop, so the payload carries **one line per criterion in TMS render
 * order** whether or not a mark exists for it — an unmarked criterion travels as
 * `value: null, na: false`, which is what it is. The outcome is recomputed here with
 * `computePerformanceOutcome`, the single implementation of the weighted formula, rather
 * than being carried in from a screen: the figure in the confirmation dialogue, the figure in
 * the payload and the figure on the marking panel are then the same figure by construction.
 */
export function buildMarksPayload(input) {
    const marks = input.criteria.map((criterion) => {
        const mark = input.marks.find((m) => m.criterion_stable_key === criterion.criterion_stable_key);
        const na = mark?.na === true;
        return {
            criterion_stable_key: criterion.criterion_stable_key,
            name: criterion.name,
            weight: criterion.weight,
            value: na ? null : (mark?.value ?? null),
            na,
        };
    });
    return {
        kind: "marks",
        subjectTmsPersonId: input.subjectTmsPersonId,
        subjectName: input.subjectName,
        tournamentId: input.tournamentId,
        gradingRoleKey: input.gradingRoleKey,
        marks,
        outcome: computePerformanceOutcome(input.criteria, input.marks),
        weightTotal: CRITERIA_WEIGHT_TOTAL,
        liveWeightTotal: input.criteria.reduce((total, criterion) => marks.find((m) => m.criterion_stable_key === criterion.criterion_stable_key)?.na === true
            ? total
            : total + criterion.weight, 0),
    };
}
export function buildReportPayload(input) {
    return {
        kind: "report",
        subjectTmsPersonId: input.subjectTmsPersonId,
        subjectName: input.subjectName,
        tournamentId: input.tournamentId,
        text: input.text,
        charCount: input.text.length,
    };
}
/* ── Completeness (the §B4 gate) ──────────────────────────────────────────── */
/**
 * The criteria a marks push would leave unstated — neither marked nor `N/A`.
 *
 * §B4's dialogue promises *"All {N} category marks and the computed Performance Outcome"*,
 * and a payload that carried three nulls would not be that. The gate is here, beside the
 * payload it judges, so the route's refusal and the screen's disabled button read the same
 * set. **A zero-weight criterion is not exempt**: `Video Umpire` is marked on the same
 * slider as everything else and printed on the report (§B3).
 */
export function unmarkedCriteria(payload) {
    return payload.marks.filter((mark) => !mark.na && mark.value === null);
}
/** True when every criterion in the set carries a mark or an `N/A`. */
export function marksPayloadIsComplete(payload) {
    return unmarkedCriteria(payload).length === 0;
}
/* ── The fallback renderings (§4) ─────────────────────────────────────────── */
/**
 * `Copy For TMS`, marks (§4): *"the exact-format text on the clipboard for the TD to paste
 * into the marking form himself"*.
 *
 * One line per criterion in the set's own render order — the order the TMS form draws — as
 * `name<TAB>value`, so the TD reads down the form and down the clipboard in step. A tab
 * rather than a comma because the criterion names carry commas and ampersands of their own,
 * and because a tab is what pastes into a form field cleanly.
 *
 * `N/A` prints as `N/A` and an unmarked criterion as `—`; neither is silently dropped, for
 * the reason the zero-weight rule exists: a row missing from a list of ten reads as a row
 * that does not exist.
 *
 * The two closing lines are the shared totals row (`marks-totals-row.ts`), so the clipboard,
 * the Word download and the marking screen say the same words about the same figures. They
 * are labelled rather than bare — a TD reading `200 of 200` on a clipboard, away from the
 * screen that produced it, has nothing to tell him it is a weight and not a score.
 */
export function formatMarksForTms(payload) {
    const lines = payload.marks.map((mark) => `${mark.name}\t${mark.na ? "N/A" : (mark.value ?? "—")}`);
    const totals = marksTotalsRow(payload);
    return [
        `${payload.subjectName} — whole-tournament marking`,
        "",
        ...lines,
        "",
        `${totals.label}\t${totals.markText}`,
        `${totals.weightLabel}\t${totals.weightValue}`,
    ].join("\n");
}
/**
 * `Copy For TMS`, report (§4, §B6).
 *
 * The text **and nothing else** — no heading, no name line, no character count. The TMS
 * marking section's text box is the paste target, and anything above the text would be pasted
 * into TMS along with it and would then have to be deleted by hand.
 */
export function formatReportForTms(payload) {
    return payload.text;
}
/** The clipboard text for either payload — the one entry point a `Copy For TMS` control needs. */
export function formatPayloadForTms(payload) {
    return payload.kind === "marks" ? formatMarksForTms(payload) : formatReportForTms(payload);
}
/**
 * A one-line summary of what a press would send — the confirmation dialogue's subject line
 * and the receipt's `payload summary` (§5.1), from one function so the receipt cannot
 * describe something other than what was confirmed.
 */
export function summarisePayload(payload) {
    if (payload.kind === "marks") {
        return (`${payload.marks.length} category marks and the computed Performance Outcome of ` +
            `${payload.outcome ?? "—"} for ${payload.subjectName}`);
    }
    return `${payload.charCount} characters of report text for ${payload.subjectName}`;
}
//# sourceMappingURL=tms-push.js.map